/**
 * Ask Casey to draft a new intake template (from New Template) or to revise
 * the one open in the editor. The brief starts a practice conversation in the
 * side panel; Casey's template tools write the template, which opens here and
 * refreshes when they finish.
 */
import { Sparkles } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { agentPanelStore } from "@/features/agents/agentPanelStore";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { ASSISTANT_NAME } from "@/features/requestsIde/model";

export type AskCaseyTarget =
  | { kind: "draft" }
  | { kind: "revise"; templateId: string; name: string };

type Props = {
  target: AskCaseyTarget | null;
  onOpenChange: (open: boolean) => void;
};

/** What the practice conversation starts with. */
export const askCaseyPrompt = (target: AskCaseyTarget, brief: string): string =>
  target.kind === "draft"
    ? `Draft a new intake template for the firm: ${brief}`
    : `Revise our intake template “${target.name}” (template_id ${target.templateId}): ${brief}`;

export const AskCaseyTemplateDialog: FC<Props> = ({ target, onOpenChange }) => {
  const { practiceScope } = useAgentSpecs();
  const [brief, setBrief] = useState("");
  const text = brief.trim();

  const send = () => {
    if (!target || !practiceScope || !text) return;
    agentPanelStore.setPendingDraft({
      agent: "practice",
      text: askCaseyPrompt(target, text)
    });
    agentPanelStore.openWith({ agent: "practice" });
    setBrief("");
    onOpenChange(false);
  };

  const draft = target?.kind !== "revise";

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 sm:max-w-md"
        data-testid="ask-casey-template"
      >
        <DialogHeader>
          <DialogTitle>
            {draft
              ? `Ask ${ASSISTANT_NAME} to Draft a Template`
              : `Ask ${ASSISTANT_NAME} to Revise “${
                  target?.kind === "revise" ? target.name : ""
                }”`}
          </DialogTitle>
          <DialogDescription>
            {draft
              ? `${ASSISTANT_NAME} builds it from the catalog's questions, so answers land on the client's record. It opens here when it's ready.`
              : "Every new intake made from it starts with the change. Intakes already sent keep theirs."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5">
          <Label htmlFor="ask-casey-template-brief">
            {draft ? "What Should It Collect?" : "What Should Change?"}
          </Label>
          <Textarea
            id="ask-casey-template-brief"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={
              draft
                ? "Everything for an O-1A, plus their publications and press"
                : "Add a section on prior visa refusals"
            }
            rows={4}
            autoFocus
            data-testid="ask-casey-template-brief"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!text || !practiceScope}
            onClick={send}
            data-testid="ask-casey-template-send"
          >
            <Sparkles /> Ask {ASSISTANT_NAME}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
